/**
 * Shared utilities for working with Budget V2 data structures
 */

import { type MonetaryValue } from 'ezmoney';

import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import {
  type Breakdown,
  type BreakdownTree,
} from 'src/core/modules/budgets/budgets-by-dimensions/models/budgetBreakdown';
import { type BudgetBranch } from 'src/core/modules/budgets/budgets-by-dimensions/models/budgetTree';
import { type Dimension } from 'src/core/modules/budgets/budgets-by-dimensions/models/dimension';
import {
  getDimensionName,
  type DimensionData,
} from 'src/core/modules/budgets/budgets-by-dimensions/models/dimensionData';
import { type BudgetBreakdown } from 'src/core/modules/budgets/models/breakdown';

import { type NodePath } from '../../../../budgets-by-dimensions/models/nodePath';

/**
 * Convert V2 breakdown format to legacy breakdown format
 * This allows V2 budgets to work with existing components that expect legacy data
 */
export const convertToLegacyBreakdown = (
  breakdown: Breakdown,
): BudgetBreakdown => ({
  used: breakdown.obligated.used,
  committed: breakdown.obligated.committed,
  usedExceeded: breakdown.exceeded.used,
  committedExceeded: breakdown.exceeded.committed,
  available: breakdown.remaining,
});

/**
 * Type for a flattened budget row ready for display
 */
export type FlattenedBudgetRow = {
  id: string;
  dimension: Dimension | 'root';
  dimensionName: string | undefined;
  label: string;
  breakdown: BudgetBreakdown;
  allocated: MonetaryValue;
  depth: number;
  hasChildren: boolean;
  nodePath: NodePath;
};

/**
 * Options for flattening a budget tree
 */
export type FlattenBudgetTreeOptions = {
  budgetName: string;
  collapsedIds?: Set<string>;
};

/**
 * Flatten a V2 budget breakdown tree into an array of displayable rows
 * This handles the recursive tree structure and converts it to a flat list
 * suitable for rendering in a UI
 */
export const flattenBudgetTree = (
  breakdowns: BreakdownTree,
  dimensionData: DimensionData,
  options: FlattenBudgetTreeOptions,
  t: TGlobalFunctionTyped,
): FlattenedBudgetRow[] => {
  const rows: FlattenedBudgetRow[] = [];
  const { budgetName, collapsedIds = new Set<string>() } = options;

  // Add root row
  const rootBreakdown = convertToLegacyBreakdown(breakdowns.outcome);
  rows.push({
    id: 'root',
    dimension: 'root',
    dimensionName: undefined,
    label: budgetName,
    breakdown: rootBreakdown,
    allocated: breakdowns.outcome.allocated,
    depth: 0,
    hasChildren: Boolean(breakdowns.branch),
    nodePath: [],
  });

  // Recursively traverse branches
  const traverseTree = (
    branch: BudgetBranch<Breakdown> | undefined,
    depth: number,
    parentId: string,
    parentNodePath: NodePath,
  ) => {
    if (!branch) {
      return;
    }

    branch.nodes.forEach((node, index) => {
      const { dimension } = branch;
      const nodeId = `${parentId}-${dimension}-${index}`;
      const nodeBreakdown = convertToLegacyBreakdown(node.outcome);
      const hasChildren = Boolean(node.branch);

      // Get dimension value label using actual dimension data
      const valueId = node.values;
      const nodePath = [...parentNodePath, index];
      const dimensionName = valueId
        ? getDimensionName(dimension, valueId, dimensionData, t)
        : undefined;

      rows.push({
        id: nodeId,
        dimension,
        dimensionName,
        label: dimensionName ?? `${dimension} ${index + 1}`,
        breakdown: nodeBreakdown,
        allocated: node.outcome.allocated,
        depth,
        hasChildren,
        nodePath,
      });

      // Recurse into nested dimension if not collapsed
      if (node.branch && !collapsedIds.has(nodeId)) {
        traverseTree(node.branch, depth + 1, nodeId, nodePath);
      }
    });
  };

  // Start traversal if root is not collapsed
  if (!collapsedIds.has('root') && breakdowns.branch) {
    traverseTree(breakdowns.branch, 1, 'root', []);
  }

  return rows;
};
